import React, { useState } from 'react';
import { OnboardingBlueprint, BlueprintSection } from './Onboarding/Types';
import { SECTION_FORMAT_TO_DISPLAY_NAME } from './Onboarding/Constants';

export interface BlueprintPreviewPanelProps {
  blueprint: OnboardingBlueprint;
  onBack: () => void;
}

const BlueprintPreviewPanel: React.FC<BlueprintPreviewPanelProps> = ({ blueprint, onBack }) => {
  const sections = blueprint.raw_info || [];

  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    () => new Set(sections.map((_, i) => i))//Default to all expanded
  );

  const toggleSection = (index: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(sections.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  return (
    <div style={{
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    }}>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 12px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.95em',
          color: '#666',
          alignSelf: 'flex-start',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#666';
        }}
      >
        ← Back to Tools
      </button>

      {/* Header */}
      <div style={{
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '12px',
      }}>
        <h2 style={{
          fontSize: '1.5em',
          fontWeight: 'bold',
          marginBottom: '4px',
        }}>
          Blueprint Preview
        </h2>
        {blueprint.title && (
          <p style={{
            fontSize: '1em',
            color: '#666',
            margin: 0,
          }}>
            {blueprint.title}
          </p>
        )}
      </div>

      {/* Sections Header with Expand/Collapse All */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
          margin: 0,
        }}>
          Sections ({sections.length})
        </h3>
        {sections.length > 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={expandAll}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8em',
                color: '#666',
              }}
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8em',
                color: '#666',
              }}
            >
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Sections List */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {sections.length === 0 ? (
          <p style={{
            color: '#999',
            fontStyle: 'italic',
          }}>
            No sections defined in this blueprint.
          </p>
        ) : (
          sections.map((section: BlueprintSection, index: number) => {
            const isExpanded = expandedSections.has(index);
            return (
              <div
                key={index}
                style={{
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden',
                }}
              >
                {/* Section Header - Clickable */}
                <div
                  onClick={() => toggleSection(index)}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: isExpanded ? '#f0f0f0' : '#f9f9f9',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isExpanded ? '#f0f0f0' : '#f9f9f9';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    {/* Expand/Collapse Icon */}
                    <span style={{
                      fontSize: '0.8em',
                      color: '#666',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}>
                      ▶
                    </span>

                    {/* Section Title */}
                    <span style={{
                      fontWeight: '600',
                      fontSize: '1em',
                      color: '#333',
                    }}>
                      {section.title}
                    </span>

                    {/* Content Type and Format Badges */}
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      color: '#555',
                    }}>
                      {section.content_type}
                    </span>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      color: '#555',
                    }}>
                      {SECTION_FORMAT_TO_DISPLAY_NAME[section.format] || section.format}
                    </span>
                  </div>
                </div>

                {/* Section Content - Expandable */}
                {isExpanded && (
                  <div style={{
                    padding: '12px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: 'white',
                  }}>
                    {section.instructions ? (
                      <div>
                        <span style={{
                          fontSize: '0.85em',
                          fontWeight: '600',
                          color: '#666',
                          display: 'block',
                          marginBottom: '8px',
                        }}>
                          Instructions:
                        </span>
                        <p style={{
                          fontSize: '0.9em',
                          color: '#333',
                          lineHeight: '1.5',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {section.instructions}
                        </p>
                      </div>
                    ) : (
                      <p style={{
                        fontSize: '0.9em',
                        color: '#999',
                        fontStyle: 'italic',
                        margin: 0,
                      }}>
                        No instructions provided for this section.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BlueprintPreviewPanel;
